import { SagaIterator } from 'redux-saga';
import { all, put, call, takeLatest, select } from 'redux-saga/effects';
import { MCAFirebaseSaga } from '../root-saga';
import {
  FirestoreSnapshot,
  OrderProps,
  UserDetails,
  GET_ORDERS,
  GET_COOKS,
  GetCooksDetailsAction,
  AcceptOrderAction,
  RateGuestAction,
  ACCEPT_ORDER,
  RATE_GUEST,
  GET_RATINGS,
  Ratings,
} from './types';
import { setLoading } from '../app-globals/actions';
import { setOrders, setCooks, getRatings, setRatings } from './actions';
import { firestore } from '../../constants/firebase';
import { getUserState } from '../user/selector';
import { sendNotification } from '../notifications/actions';

function* fetchOrders(): SagaIterator<void> {
  try {
    yield put(setLoading(true, 'Fetching Orders'));
    const state = yield select();
    const { userReducer } = state;
    const { user } = userReducer;

    const orderRef = firestore
      .collection('orders')
      .orderBy('timeadded', 'desc');
    const ordersSnapshot = yield call(
      MCAFirebaseSaga.firestore.getCollection,
      orderRef
    );
    const mealSnapshot = yield call(
      MCAFirebaseSaga.firestore.getCollection,
      'meals'
    );

    const userSnapshot = yield call(
      MCAFirebaseSaga.firestore.getCollection,
      'users'
    );

    const orders: OrderProps[] = [];
    let userinfo: UserDetails = {
      id: '',
      firstName: '',
      address: {
        city: '',
        note: '',
        street: '',
      },
      image: '',
      phone: '',
      type: '',
      location: {
        lat: 0,
        lng: 0,
      },
      description: '',
    };
    if (user.accountType === 'i seek') {
      ordersSnapshot.forEach((items: FirestoreSnapshot) => {
        userSnapshot.forEach((useritems: FirestoreSnapshot) => {
          mealSnapshot.forEach((mealitems: FirestoreSnapshot) => {
            if (
              items.data().mealid === mealitems.id &&
              items.data().guest === user.userid &&
              items.data().cook === useritems.data().userid
            ) {
              userinfo = {
                id: user.userid,
                firstName: user.firstName,
                address: user.address,
                image: user.image,
                phone: user.phone,
                type: user.accountType,
                location: user.location,
                description: user.description,
              };

              const cookDetails = {
                id: useritems.data().userid,
                firstName: useritems.data().firstName,
                address: useritems.data().address,
                image: useritems.data().image,
                phone: useritems.data().phone,
                type: useritems.data().accountType,
                location: useritems.data().location,
              };

              const body = {
                id: items.id,
                mealid: mealitems.id,
                ...items.data(),
                title: mealitems.data().title,
                description: mealitems.data().description,
                image: mealitems.data().image,
                cook: items.data().cook,
                consumption: items.data().consumption,
                timeadded: items.data().timeadded,
                userdetails: userinfo,
                cookDetails,
              };
              orders.push(body);
            }
          });
        });
      });
    } else {
      ordersSnapshot.forEach((items: FirestoreSnapshot) => {
        userSnapshot.forEach((useritems: FirestoreSnapshot) => {
          mealSnapshot.forEach((mealitems: FirestoreSnapshot) => {
            if (
              items.data().mealid === mealitems.id &&
              items.data().cook === user.userid &&
              items.data().guest === useritems.data().userid
            ) {
              userinfo = {
                id: useritems.data().userid,
                firstName: useritems.data().firstName,
                address: useritems.data().address,
                image: useritems.data().image,
                phone: useritems.data().phone,
                type: useritems.data().accountType,
                location: useritems.data().location,
                description: useritems.data().description,
              };

              const body = {
                id: items.id,
                mealid: mealitems.id,
                ...items.data(),
                title: mealitems.data().title,
                description: mealitems.data().description,
                image: mealitems.data().image,
                cook: items.data().cook,
                consumption: items.data().consumption,
                timeadded: items.data().timeadded,
                userdetails: userinfo,
              };
              orders.push(body);
            }
          });
        });
      });
    }
    yield put(setOrders(orders));
    yield put(getRatings());
    yield put(setLoading(false, ''));
  } catch (e) {
    yield put(setLoading(false, ''));
  }
}

function* getCooks({ userid }: GetCooksDetailsAction): SagaIterator<void> {
  try {
    yield put(setLoading(true, 'Fetching Cooks'));
    const cooks = firestore.collection('users').where('userid', '==', userid);
    const cooksSnapshot = yield call(
      MCAFirebaseSaga.firestore.getCollection,
      cooks
    );
    let userinfo: UserDetails = {
      id: '',
      firstName: '',
      address: {
        city: '',
        note: '',
        street: '',
      },
      image: '',
      phone: '',
      type: '',
      location: {
        lat: 0,
        lng: 0,
      },
      description: '',
    };
    cooksSnapshot.forEach((cookitems: FirestoreSnapshot) => {
      userinfo = {
        id: cookitems.data().userid,
        firstName: cookitems.data().firstName,
        address: cookitems.data().address,
        image: cookitems.data().image,
        phone: cookitems.data().phone,
        type: cookitems.data().accountType,
        location: cookitems.data().location,
        description: cookitems.data().description,
      };
    });
    yield put(setCooks(userinfo));
    yield put(setLoading(false, ''));
  } catch (e) {
    yield put(setLoading(false, ''));
  }
}

function* acceptOrRefuseOrder({
  orderid,
  status,
  refusedmessage,
  guestid,
}: AcceptOrderAction): SagaIterator<void> {
  try {
    yield put(setLoading(true, `${status} order`));
    const body = {
      status: status,
      refusedmessage: refusedmessage,
    };
    yield call(
      MCAFirebaseSaga.firestore.setDocument,
      `orders/${orderid}`,
      body,
      { merge: true }
    );
    const send = {
      action: status,
      reason: '',
      mealname: '',
      order: orderid,
    };
    yield put(sendNotification(send, guestid));
    yield put(setLoading(false, ''));
  } catch (e) {
    yield put(setLoading(false, `Error ${status} order`));
  }
}

function* rateGuest({ ratings }: RateGuestAction): SagaIterator<void> {
  try {
    yield put(setLoading(true, 'Submit Rating'));
    const body = {
      ...ratings,
    };
    yield call(MCAFirebaseSaga.firestore.addDocument, 'rating', body);
    yield put(setLoading(false, ''));
  } catch (e) {
    yield put(setLoading(false, 'Error while submitting the rate'));
  }
}

function* getUserDetails(id: string, item: Ratings) {
  try {
    const firestoreGetDocument = MCAFirebaseSaga.firestore.getDocument;
    const usersnapshot = yield call(firestoreGetDocument, `users/${id}`);
    const userdata = usersnapshot.data();
    const user = {
      id: userdata.userid,
      firstName: userdata.firstName,
      address: userdata.address,
      image: userdata.image,
      phone: userdata.phone,
      type: userdata.accountType,
    };

    const body = { ...item, userdetails: user };
    return body;
  } catch (e) {
    // console.log(e);
  }
}

function* fetchRatings(): SagaIterator<void> {
  try {
    const { user } = yield select(getUserState);
    const firestoreGet = MCAFirebaseSaga.firestore.getCollection;

    const ratingRef = firestore.collection('rating');
    const myRateesRef = ratingRef.where('ratedby', '==', user.userid);
    const myRatingsRef = ratingRef.where('user', '==', user.userid);

    const [myRatees, myRatings] = yield all([
      call(firestoreGet, myRateesRef),
      call(firestoreGet, myRatingsRef),
    ]);

    const ratings: Ratings[] = [];

    myRatees.forEach((item: FirestoreSnapshot) => {
      const timeadded = item.data().timeadded.toDate();
      delete item.data().timeadded;
      const body = {
        ...item.data(),
        timeadded,
      };
      ratings.push(body);
    });

    myRatings.forEach((item: FirestoreSnapshot) => {
      const timeadded = item.data().timeadded.toDate();
      delete item.data().timeadded;
      const body = {
        ...item.data(),
        timeadded,
      };
      ratings.push(body);
    });

    const fetchedData = yield all(
      ratings.map(item => {
        return call(getUserDetails, item.ratedby, item);
      })
    );

    yield put(setRatings(fetchedData));
  } catch (e) {
    setLoading(false, '');
  }
}

export function* OrderSaga(): SagaIterator {
  yield all([
    takeLatest(GET_ORDERS, fetchOrders),
    takeLatest(GET_COOKS, getCooks),
    takeLatest(ACCEPT_ORDER, acceptOrRefuseOrder),
    takeLatest(RATE_GUEST, rateGuest),
    takeLatest(GET_RATINGS, fetchRatings),
  ]);
}
